import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, LockKeyhole } from "lucide-react";

// Provider SPID di esempio (schermata familiare, con banner "ambiente demo")
const PROVIDERS = [
  { id: "poste", name: "PosteID", color: "#F6A800" },
  { id: "aruba", name: "Aruba ID", color: "#00A651" },
  { id: "spiditalia", name: "SpidItalia", color: "#009EE3" },
  { id: "namirial", name: "Namirial ID", color: "#1D1D1B" },
  { id: "infocert", name: "InfoCert ID", color: "#E30613" },
  { id: "lepida", name: "Lepida ID", color: "#003A70" },
  { id: "sielte", name: "Sielte ID", color: "#004A99" },
  { id: "tim", name: "TIM ID", color: "#003399" },
  { id: "teamsystem", name: "TeamSystem ID", color: "#005CA9" },
];

export default function SpidSimulazione() {
  const [params] = useSearchParams();
  const sessionToken = params.get("session") || "";
  const { refresh } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState("provider"); // provider | login | confirm | processing
  const [provider, setProvider] = useState(null);
  const [cf, setCf] = useState("");
  const [pw, setPw] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const doCallback = async () => {
    setLoading(true);
    try {
      const r = await api.post("/delega/callback", {
        session_token: sessionToken,
        codice_fiscale: cf.trim().toUpperCase(),
      });
      await refresh();
      toast.success(r.data.message || "Delega attivata");
      nav(`/delega/successo?scaricate=${r.data.fatture_scaricate || 0}`);
    } catch (ex) {
      toast.error(formatErr(ex.response?.data?.detail));
      setLoading(false);
    }
  };

  if (!sessionToken) {
    return (
      <div className="min-h-screen bg-white grid place-items-center p-6">
        <div className="text-center">
          <p className="text-[#6B6B6B]">Sessione SPID mancante.</p>
          <Link to="/autorizza-fatture" className="mt-4 inline-block text-[#0066CC] underline">Torna indietro</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Banner demo */}
      <div className="bg-[#FFF3CD] border-b border-[#F5D982] px-4 py-2 text-center text-xs text-[#664D03]" data-testid="spid-demo-banner">
        <strong>Ambiente demo</strong> — questa è una simulazione del flusso SPID. Nessuna credenziale reale viene richiesta o memorizzata. In produzione verrai reindirizzato al vero Identity Provider tramite un fornitore accreditato AgID.
      </div>

      {/* Header istituzionale */}
      <header className="border-b border-[#DEE2E6] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#0066CC] grid place-items-center">
              <span className="text-white font-bold text-lg">SP</span>
            </div>
            <div>
              <div className="font-bold text-[#0066CC]">spid</div>
              <div className="text-[10px] text-[#6C757D] uppercase tracking-wider">Sistema Pubblico di Identità Digitale</div>
            </div>
          </div>
          <div className="text-xs text-[#6C757D] hidden sm:block">agid.gov.it (simulato)</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {step === "provider" && (
          <div>
            <div className="text-center mb-8">
              <LockKeyhole className="mx-auto text-[#0066CC]" size={32}/>
              <h1 className="text-2xl font-semibold mt-3 text-[#212529]">Autorizzazione IVA Radar</h1>
              <p className="mt-2 text-sm text-[#495057]">Per proseguire con la delega di accesso al Cassetto Fiscale, scegli il tuo Identity Provider SPID:</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PROVIDERS.map(p => (
                <button key={p.id} data-testid={`spid-provider-${p.id}`} onClick={()=>{setProvider(p); setStep("login");}}
                  className="border border-[#DEE2E6] rounded-md py-4 px-3 hover:border-[#0066CC] hover:bg-[#F8F9FA] transition-colors text-center">
                  <div className="w-10 h-10 rounded-sm mx-auto mb-2" style={{background: p.color}}/>
                  <div className="text-sm font-medium text-[#212529]">{p.name}</div>
                </button>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/autorizza-fatture" data-testid="spid-cancel" className="text-sm text-[#6C757D] underline">Annulla</Link>
            </div>
          </div>
        )}

        {step === "login" && provider && (
          <div className="max-w-md mx-auto">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-8 h-8 rounded-sm" style={{background: provider.color}}/>
              <div>
                <div className="text-xs text-[#6C757D]">Accesso via</div>
                <div className="font-semibold text-[#212529]">{provider.name}</div>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-[#212529] mb-1">Accedi con {provider.name}</h2>
            <p className="text-sm text-[#6C757D] mb-6">Inserisci le tue credenziali per proseguire</p>
            <form onSubmit={(e)=>{e.preventDefault(); if(cf.length>=16) setStep("confirm");}} className="space-y-4">
              <div>
                <label className="text-xs text-[#495057] block mb-1">Codice Fiscale</label>
                <input data-testid="spid-cf" required maxLength={16} minLength={16} value={cf} onChange={e=>setCf(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-[#DEE2E6] rounded-md focus:outline-none focus:border-[#0066CC] font-mono uppercase"
                  placeholder="RSSMRA80A01H501U"/>
              </div>
              <div>
                <label className="text-xs text-[#495057] block mb-1">Password SPID</label>
                <input data-testid="spid-password" type="password" required value={pw} onChange={e=>setPw(e.target.value)}
                  className="w-full px-3 py-2 border border-[#DEE2E6] rounded-md focus:outline-none focus:border-[#0066CC]"
                  placeholder="••••••••"/>
                <p className="text-[10px] text-[#6C757D] mt-1 italic">Ambiente demo · qualsiasi valore va bene</p>
              </div>
              <div>
                <label className="text-xs text-[#495057] block mb-1">Codice OTP (SMS/App)</label>
                <input data-testid="spid-otp" required value={otp} onChange={e=>setOtp(e.target.value)}
                  className="w-full px-3 py-2 border border-[#DEE2E6] rounded-md focus:outline-none focus:border-[#0066CC] font-mono tracking-widest"
                  placeholder="123456"/>
              </div>
              <button data-testid="spid-login" type="submit" className="w-full py-3 bg-[#0066CC] text-white rounded-md hover:bg-[#004E9E] font-medium">
                Prosegui
              </button>
              <button type="button" onClick={()=>setStep("provider")} className="w-full py-2 text-sm text-[#6C757D] hover:text-[#0066CC]">Cambia provider</button>
            </form>
          </div>
        )}

        {step === "confirm" && (
          <div className="max-w-lg mx-auto">
            <div className="mb-6 flex items-center gap-2 text-sm text-[#28A745]">
              <ShieldCheck size={18}/> Identità verificata: <span className="font-mono">{cf}</span>
            </div>
            <div className="border border-[#DEE2E6] rounded-md p-6">
              <h2 className="text-xl font-semibold text-[#212529]">Autorizzazione richiesta</h2>
              <p className="mt-2 text-sm text-[#495057]"><strong>IVA Radar S.r.l.</strong> richiede l'autorizzazione ad accedere ai seguenti dati del tuo Cassetto Fiscale:</p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0066CC]"/>
                  <span>Fatture elettroniche emesse e ricevute (SDI)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0066CC]"/>
                  <span>Dati anagrafici P.IVA e attività</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0066CC]"/>
                  <span>Comunicazioni periodiche IVA (solo lettura)</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-[#F8F9FA] border border-[#DEE2E6] rounded-md text-xs text-[#495057]">
                Durata delega: 24 mesi · Puoi revocarla in ogni momento dalle Impostazioni di IVA Radar o dal tuo Cassetto Fiscale.
              </div>
              <div className="mt-6 flex gap-3">
                <button data-testid="spid-confirm" onClick={doCallback} disabled={loading}
                  className="flex-1 py-3 bg-[#0066CC] text-white rounded-md hover:bg-[#004E9E] font-medium disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? "Attivazione…" : <>Autorizza <ArrowRight size={16}/></>}
                </button>
                <button onClick={()=>setStep("provider")} className="px-4 py-3 border border-[#DEE2E6] rounded-md text-[#495057] hover:bg-[#F8F9FA]">Rifiuta</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <footer className="border-t border-[#DEE2E6] mt-12 py-6">
        <div className="max-w-4xl mx-auto px-6 text-xs text-[#6C757D] flex justify-between">
          <span>SPID · Agenzia per l'Italia Digitale (simulato)</span>
          <span>Ambiente demo · IVA Radar</span>
        </div>
      </footer>
    </div>
  );
}
